<template>
  <section class="admin-content" id="establishment-dashboard">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 12px;">
            <div class="header-icon-wrap"><i class="fe fe-briefcase" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <h4 class="mb-0 font-weight-bold">Establishment Dashboard</h4>
              <small style="opacity: 0.8;">Ministries, departments and agencies: who is covered and how they use care</small>
            </div>
            <router-link to="/mda-manager" class="btn btn-light btn-sm font-weight-bold px-3"><i class="fe fe-list mr-1"></i> Establishments</router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="card shadow-sm table-card mb-3">
            <div class="card-body d-flex flex-wrap align-items-center" style="gap: 10px;">
              <PeriodPicker v-model="period" @change="load" default-preset="12m" />
              <v-select class="sector-select" multiple v-model="sectors" :options="sectorOptions" placeholder="All programmes" @input="load" />
              <small class="text-muted ml-auto">New members, encounters and claims are counted in the period; members are everyone covered today.</small>
            </div>
          </div>

          <div v-if="loading && !d" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
          <template v-else-if="d">
            <div class="row">
              <div class="col-6 col-lg-3 mb-3" v-for="c in cards" :key="c.label">
                <div class="card shadow-sm h-100 table-card"><div class="card-body py-3">
                  <span class="stat-label">{{ c.label }}</span>
                  <div class="stat-value">{{ c.value }}</div>
                  <small class="text-muted">{{ c.note }}</small>
                </div></div>
              </div>
            </div>

            <div v-if="d.totals.formal_without_establishment" class="alert alert-warning small py-2">
              <i class="fe fe-alert-triangle mr-1"></i>
              {{ fmt(d.totals.formal_without_establishment) }} formal-sector enrollees have no establishment (or one that is not on the list), so they are not counted here.
            </div>

            <div class="row">
              <div class="col-lg-7 mb-4">
                <div class="card shadow-sm table-card h-100">
                  <div class="form-card-header">
                    <div class="section-number"><i class="fe fe-bar-chart-2" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white flex-grow-1">Largest establishments</h6>
                    <select class="form-control form-control-sm" style="width: auto;" v-model="chartMeasure">
                      <option value="members">Members</option><option value="new_members">New members</option>
                      <option value="encounters">Encounters</option><option value="claims_amount">Amount claimed</option>
                    </select>
                  </div>
                  <div class="card-body">
                    <apexchart v-if="topRows.length" type="bar" :height="Math.max(260, topRows.length * 30)" :options="barOptions" :series="barSeries" :key="'b' + chartKey + chartMeasure" />
                    <div v-else class="empty-state">No data for this period.</div>
                  </div>
                </div>
              </div>
              <div class="col-lg-5 mb-4">
                <div class="card shadow-sm table-card h-100">
                  <div class="form-card-header">
                    <div class="section-number"><i class="fe fe-trending-up" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white">New members per month</h6>
                  </div>
                  <div class="card-body">
                    <apexchart type="area" height="300" :options="trendOptions" :series="trendSeries" :key="'t' + chartKey" />
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm table-card">
              <div class="d-flex flex-wrap align-items-center px-4 pt-4 pb-3" style="gap: 8px;">
                <i class="fe fe-list text-primary mr-1"></i>
                <h6 class="mb-0 font-weight-bold">All establishments</h6>
                <span class="badge badge-soft-info">{{ filteredRows.length }}</span>
                <input v-model.trim="search" class="form-control form-control-sm ml-auto" style="max-width: 280px;" placeholder="Search establishment…" />
                <button class="btn btn-sm btn-outline-primary" @click="exportCsv"><i class="fe fe-download mr-1"></i> CSV</button>
              </div>
              <div class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead class="table-head">
                    <tr>
                      <th class="table-th sortable" v-for="h in headers" :key="h.key" :class="h.right ? 'text-right' : ''" @click="sortBy(h.key)">
                        {{ h.label }} <i v-if="sort.key === h.key" class="fe" :class="sort.desc ? 'fe-chevron-down' : 'fe-chevron-up'"></i>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!filteredRows.length"><td :colspan="headers.length" class="empty-state">No establishment matches.</td></tr>
                    <tr class="table-row" v-for="r in sortedRows" :key="r.id">
                      <td><router-link :to="'/mda/' + r.id">{{ r.name }}</router-link></td>
                      <td class="text-right font-weight-bold">{{ fmt(r.members) }}</td>
                      <td class="text-right">{{ fmt(r.principals) }}</td>
                      <td class="text-right">{{ fmt(r.dependants) }}</td>
                      <td class="text-right">{{ fmt(r.new_members) }}</td>
                      <td class="text-right">{{ pct(r.active_pct) }}</td>
                      <td class="text-right">{{ pct(r.female_pct) }}</td>
                      <td class="text-right">{{ fmt(r.encounters) }}</td>
                      <td class="text-right">{{ r.encounters_per_1000 === null ? "—" : r.encounters_per_1000 }}</td>
                      <td class="text-right">{{ fmt(r.codes) }}</td>
                      <td class="text-right">{{ fmt(r.claims) }}</td>
                      <td class="text-right">₦{{ fmt(r.claims_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import PeriodPicker from "./components/PeriodPicker.vue";

/** Establishments (MDAs): members, new members, active cover, utilisation and claims (GET analytics/establishments). */
export default {
  components: { Navbar, PeriodPicker },
  data() {
    return {
      period: { from: "", to: "" }, sectors: [], sectorOptions: [], d: null, loading: false, chartKey: 0,
      chartMeasure: "members", search: "", sort: { key: "members", desc: true },
      headers: [
        { key: "name", label: "Establishment" }, { key: "members", label: "Members", right: true }, { key: "principals", label: "Principals", right: true },
        { key: "dependants", label: "Dependants", right: true }, { key: "new_members", label: "New", right: true }, { key: "active_pct", label: "Active cover", right: true },
        { key: "female_pct", label: "Female", right: true }, { key: "encounters", label: "Encounters", right: true }, { key: "encounters_per_1000", label: "Per 1,000", right: true },
        { key: "codes", label: "Codes", right: true }, { key: "claims", label: "Claims", right: true }, { key: "claims_amount", label: "Claimed", right: true },
      ],
    };
  },
  computed: {
    cards() {
      const t = this.d.totals;
      return [
        { label: "Establishments", value: this.fmt(t.establishments), note: `${this.fmt(t.with_members)} with members` },
        { label: "Members covered", value: this.fmt(t.members), note: `${this.fmt(t.principals)} principals · ${this.fmt(t.dependants)} dependants` },
        { label: "New in the period", value: this.fmt(t.new_members), note: "principals and dependants" },
        { label: "Encounters per 1,000", value: t.encounters_per_1000 === null ? "—" : t.encounters_per_1000, note: `${this.fmt(t.encounters)} encounters · ₦${this.fmt(t.claims_amount)} claimed` },
      ];
    },
    filteredRows() {
      const s = this.search.toLowerCase();
      return s ? this.d.rows.filter((r) => r.name.toLowerCase().includes(s)) : this.d.rows;
    },
    sortedRows() {
      const { key, desc } = this.sort;
      return this.filteredRows.slice().sort((a, b) => {
        const x = a[key] === null ? -Infinity : a[key];
        const y = b[key] === null ? -Infinity : b[key];
        const c = typeof x === "string" ? x.localeCompare(y) : x - y;
        return desc ? -c : c;
      });
    },
    topRows() {
      return this.d.rows.filter((r) => r[this.chartMeasure] > 0).sort((a, b) => b[this.chartMeasure] - a[this.chartMeasure]).slice(0, 15);
    },
    barSeries() {
      return [{ name: { members: "Members", new_members: "New members", encounters: "Encounters", claims_amount: "Amount claimed" }[this.chartMeasure], data: this.topRows.map((r) => r[this.chartMeasure]) }];
    },
    barOptions() {
      const money = this.chartMeasure === "claims_amount";
      return {
        chart: { toolbar: { show: false } }, colors: ["#2563eb"],
        plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 3 } },
        xaxis: { categories: this.topRows.map((r) => r.name), labels: { formatter: (v) => (money ? "₦" : "") + this.fmt(v) } },
        dataLabels: { enabled: true, formatter: (v) => (money ? "₦" : "") + this.fmt(v), style: { colors: ["#fff"] } },
        tooltip: { y: { formatter: (v) => (money ? "₦" : "") + this.fmt(v) } },
      };
    },
    trendSeries() { return [{ name: "New members", data: this.d.trend.map((p) => p.new_members) }]; },
    trendOptions() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } }, colors: ["#16a34a"], stroke: { curve: "smooth", width: 3 },
        xaxis: { categories: this.d.trend.map((p) => p.label), labels: { rotate: -45, hideOverlappingLabels: true } },
        yaxis: { labels: { formatter: (v) => this.fmt(v) } }, dataLabels: { enabled: true },
        fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
      };
    },
  },
  mounted() {
    this.$batchGet("/api/v1/auth/analytics/enrollment/sectors").then((res) => {
      this.sectorOptions = (res.data.data.rows || []).map((r) => r.sector).filter((s) => s && s !== "Not set");
    }).catch(() => {});
  },
  methods: {
    fmt(v) { return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
    pct(v) { return v === null || v === undefined ? "—" : v + "%"; },
    sortBy(key) { this.sort = { key, desc: this.sort.key === key ? !this.sort.desc : key !== "name" }; },
    load() {
      if (!this.period.from) return;
      this.loading = true;
      this.axios.get("/api/v1/auth/analytics/establishments", { params: { from: this.period.from, to: this.period.to, sectors: this.sectors } })
        .then((res) => { this.d = res.data.data; this.chartKey++; })
        .catch(() => this.$toasted.error("Could not load the establishments.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.loading = false; });
    },
    exportCsv() {
      const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const lines = [this.headers.map((h) => esc(h.label)).join(",")].concat(this.sortedRows.map((r) => this.headers.map((h) => esc(r[h.key])).join(",")));
      const url = URL.createObjectURL(new Blob(["﻿" + lines.join("\n")], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `establishments_${this.period.from}_${this.period.to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
.sector-select { min-width: 220px; max-width: 420px; font-size: 0.85rem; }
.sortable { cursor: pointer; white-space: nowrap; }
</style>
